import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal, MaskedLines } from "@/components/motion/Reveal";

export const SectionHeading = ({ eyebrow, title, intro, action, id, className }) => (
  <Reveal className={cn("mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between lg:mb-14", className)}>
    <div className="max-w-2xl">
      <p className="eyebrow">{eyebrow}</p>
      <h2 id={id} className="mt-4 font-display text-3xl font-extrabold leading-[1.04] tracking-tight sm:text-4xl lg:text-[2.75rem]">
        {title}
      </h2>
      {intro && <p className="mt-4 text-base leading-relaxed text-steel">{intro}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </Reveal>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb">
    <ol className="flex flex-wrap items-center gap-1.5 text-sm text-steel">
      {items.map((c, i) => (
        <li key={c.label} className="inline-flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
          {c.to ? (
            <Link to={c.to} className="link-underline hover:text-ink" data-testid={`breadcrumb-${i}`}>
              {c.label}
            </Link>
          ) : (
            <span aria-current="page" className="font-semibold text-ink">
              {c.label}
            </span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export const PageHeader = ({ crumbs, eyebrow, title, intro, children, aside }) => (
  <section className="relative overflow-hidden border-b border-line bg-paper">
    <div className="container-x py-10 sm:py-14 lg:py-20">
      <Breadcrumbs items={crumbs} />
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <p className="eyebrow mt-8 lg:mt-12">{eyebrow}</p>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskedLines lines={title} />
          </h1>
          {intro && (
            <Reveal delay={0.25} y={16}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-steel md:text-lg">{intro}</p>
            </Reveal>
          )}
          {children}
        </div>
        {aside && (
          <Reveal delay={0.35} y={16} className="hidden sm:block lg:col-span-4">
            {aside}
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

export const SlashCorner = ({ className }) => (
  <svg viewBox="0 0 88 88" className={cn("cut-frame__slash", className)} aria-hidden="true">
    <polygon points="0,36 52,88 40,88 0,48" fill="#0A52C2" />
  </svg>
);

export const CutImage = ({ src, alt, className, imgClassName, eager }) => (
  <div className={cn("cut-frame", className)}>
    <div className="cut-frame__clip h-full overflow-hidden bg-paper">
      <img src={src} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" className={cn("h-full w-full object-cover", imgClassName)} />
    </div>
    <SlashCorner />
  </div>
);
